import { type ReportTemplate } from 'reportingSolution/constants'

const config: ReportTemplate = {
  id: 'arima_plus_report',
  active: true,
  label: 'Forecasting report',
  category: 'Forecasting',
  description:
    'The Alvie forecasting report includes the Arima Plus ML model and TimesFM AI model, to forecast cost and any conversions from ga4, dv360, Google Ads, Bing, Meta or any data from google sheets',
  image_url:
    'https://lh3.googleusercontent.com/drive-viewer/AK7aPaBbEhW7B9TF1_WMdsTT8OsvSNcIQjm47Tmp6ugFAcGC1nXbtUMCkG2BB0ZNwpOLjJhGBXbut-FlPh64US-Hlb88meYNIg=s1600',
  authors: [
    {
      name: 'Tobias Hink',
      email: 'hink@precisdigital.com',
      image_url: 'https://lh3.googleusercontent.com/a-/AOh14GjyNEc4WJk3HgY11avoS-2opMitZKNbLBZcaWvS=s126-p-k-rw-no'
    }
  ],
  documentation: {
    type: 'knowledge_hub',
    link: 'https://help.alvie.io/en/articles/174316-arima-plus-forecasting-report-overview',
    link_description: 'Knowledge Hub: Arima Plus Report'
  },
  dashboard: {
    template_report_id: '8f9f6c45-163c-4899-8f87-dc39101dd921',
    report_name_template: 'copy of Alvie Forecasting Report - {today}',
    datasources: [
      {
        alias: 'forecast',
        datasourceName: '{name} - Forecasting Output',
        type: 'TABLE',
        tableId: 'arima_plus_output_{name}',
        isPartitioned: 'true',
        refreshFields: 'false'
      }
    ]
  },
  sources: {
    ga4: {
      reports: [
        'ga4_session_scoped_attribution',
        'ga4_event_scoped_attribution',
        'lnda_campaign_conversion_base',
        'dda_campaign_conversion_base'
      ],
      optional: true
    },
    google_analytics: {
      reports: ['campaign_performance_hostname'],
      optional: true
    },
    gads: {
      reports: ['campaign_performance_base', 'campaign_conversion_expanded'],
      optional: true
    },
    bing: {
      reports: ['campaign_performance_base'],
      optional: true
    },
    facebook: {
      reports: ['ad_performance'],
      optional: true
    },
    dv360: {
      reports: ['campaign_performance_base'],
      optional: true
    },
    snapchat: {
      reports: ['campaign_performance_base'],
      optional: true
    },
    tiktok: {
      reports: ['ad_performance_base', 'ad_performance_custom_conversions'],
      optional: true
    },
    criteo: {
      reports: ['ad_group_performance'],
      optional: true
    },
    amazon: {
      reports: ['unified_campaign_daily_report'],
      optional: true
    },
    pinterest: {
      reports: ['ad_group_country_performance'],
      optional: true
    },
    google_sheets: {
      reports: ['forecasting_report_training_data'],
      optional: true
    },
    chatgpt_ads: {
      reports: ['ad_performance_base'],
      optional: true,
      internalOnly: true
    }
  },
  inputs: {
    ga4: [
      {
        id: 'ga4CustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Google Analytics 4.',
        required: true,
        mutable: true,
        dimensions: [
          {
            label: 'Property name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Property ID',
            value: 'property_id',
            type: 'string'
          },
          {
            label: 'Stream name',
            value: 'streamName',
            type: 'string'
          },
          {
            label: 'Stream ID',
            value: 'streamId',
            type: 'string'
          },
          {
            label: 'Hostname',
            value: 'hostname',
            type: 'string'
          },
          {
            label: 'Source',
            value: 'source',
            type: 'string'
          },
          {
            label: 'Medium',
            value: 'medium',
            type: 'string'
          },
          {
            label: 'Source / Medium',
            value: 'source_medium',
            type: 'string'
          },
          {
            label: 'Campaign',
            value: 'campaign',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign_id',
            type: 'string'
          },
          {
            label: 'Country',
            value: 'country',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Direct',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: '(direct)'
                }
              ]
            }
          },
          {
            name: 'Affiliates',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'medium',
                  value: 'affiliate'
                }
              ]
            }
          },
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'display / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'remarketing'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'display / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'prospecting'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'display'
                }
              ]
            }
          },
          {
            name: 'Organic Search',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'google'
                },
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'bing'
                },
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'yahoo'
                },
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'duckduckgo'
                }
              ],
              '1': [
                {
                  operator: 'equals',
                  dimension: 'medium',
                  value: 'organic'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Brand',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'brand'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Shopping',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'shopping'
                }
              ]
            }
          },
          {
            name: 'Paid Search - PMax',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'pmax'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'performance max'
                }
              ]
            }
          },
          {
            name: 'Paid Search - nonBrand',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                }
              ]
            }
          },
          {
            name: 'Paid Social - Retargeting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpm'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'retargeting'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'remarketing'
                }
              ]
            }
          },
          {
            name: 'Paid Social - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpm'
                }
              ]
            }
          },
          {
            name: 'Organic Social',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'facebook'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'instagram'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'pinterest'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'snapchat'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'tiktok'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'linkedin'
                }
              ]
            }
          },
          {
            name: 'Email',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'medium',
                  value: 'email'
                }
              ]
            }
          },
          {
            name: 'Referral',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'medium',
                  value: 'referral'
                }
              ]
            }
          },
          {
            name: 'Price Comparison Sites',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'idealo'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'billiger'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'kelkoo'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'bestcheck'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'pricerunner'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'prisjakt'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'ga4MarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Google Analytics 4.',
        required: true,
        mutable: true,
        dimensions: [
          {
            label: 'Property name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Property ID',
            value: 'property_id',
            type: 'string'
          },
          {
            label: 'Stream name',
            value: 'streamName',
            type: 'string'
          },
          {
            label: 'Stream ID',
            value: 'streamId',
            type: 'string'
          },
          {
            label: 'Hostname',
            value: 'hostname',
            type: 'string'
          },
          {
            label: 'Source',
            value: 'source',
            type: 'string'
          },
          {
            label: 'Medium',
            value: 'medium',
            type: 'string'
          },
          {
            label: 'Source / Medium',
            value: 'source_medium',
            type: 'string'
          },
          {
            label: 'Campaign',
            value: 'campaign',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign_id',
            type: 'string'
          },
          {
            label: 'Country',
            value: 'country',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-dk-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'denmark'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-se-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'sweden'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-no-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'norway'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-fi-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'finland'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-de-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'germany'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    google_analytics: [
      {
        id: 'googleAnalyticsCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Google Analytics.',
        required: false,
        mutable: true,
        dimensions: [
          {
            value: 'view_or_property_id',
            label: 'View ID',
            type: 'string'
          },
          {
            value: 'hostname',
            label: 'Hostname',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          },
          {
            value: 'source',
            label: 'Source',
            type: 'string'
          },
          {
            value: 'medium',
            label: 'Medium',
            type: 'string'
          },
          {
            value: 'source_medium',
            label: 'Source / Medium',
            type: 'string'
          },
          {
            value: 'web_property_id',
            label: 'Web Property Id',
            type: 'string'
          },
          {
            value: 'account_id',
            label: 'Account Id',
            type: 'string'
          },
          {
            value: 'profile_name',
            label: 'profile name',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Direct',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: '(direct)'
                }
              ]
            }
          },
          {
            name: 'Affiliates',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'medium',
                  value: 'affiliate'
                }
              ]
            }
          },
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'display / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'remarketing'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'display / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'prospecting'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'display'
                }
              ]
            }
          },
          {
            name: 'Organic Search',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'google'
                },
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'bing'
                },
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'yahoo'
                },
                {
                  operator: 'equals',
                  dimension: 'source',
                  value: 'duckduckgo'
                }
              ],
              '1': [
                {
                  operator: 'equals',
                  dimension: 'medium',
                  value: 'organic'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Brand',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'brand'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Shopping',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'shopping'
                }
              ]
            }
          },
          {
            name: 'Paid Search - PMax',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'pmax'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'performance max'
                }
              ]
            }
          },
          {
            name: 'Paid Search - nonBrand',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'google / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'bing / cpc'
                }
              ]
            }
          },
          {
            name: 'Paid Social - Retargeting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpm'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'retargeting'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'remarketing'
                }
              ]
            }
          },
          {
            name: 'Paid Social - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'facebook / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'pinterest / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'tiktok / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'snapchat / cpm'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpc'
                },
                {
                  operator: 'equals',
                  dimension: 'source_medium',
                  value: 'linkedin / cpm'
                }
              ]
            }
          },
          {
            name: 'Organic Social',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'facebook'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'instagram'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'pinterest'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'snapchat'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'tiktok'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'linkedin'
                }
              ]
            }
          },
          {
            name: 'Email',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'medium',
                  value: 'email'
                }
              ]
            }
          },
          {
            name: 'Referral',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'medium',
                  value: 'referral'
                }
              ]
            }
          },
          {
            name: 'Price Comparison Sites',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'idealo'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'billiger'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'kelkoo'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'bestcheck'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'pricerunner'
                },
                {
                  operator: 'contains',
                  dimension: 'source',
                  value: 'prisjakt'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'googleAnalyticsMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Google Analytics.',
        required: false,
        mutable: true,
        dimensions: [
          {
            value: 'view_or_property_id',
            label: 'View ID',
            type: 'string'
          },
          {
            value: 'hostname',
            label: 'Hostname',
            type: 'string'
          },
          {
            value: 'campaign',
            label: 'Campaign',
            type: 'string'
          },
          {
            value: 'source',
            label: 'Source',
            type: 'string'
          },
          {
            value: 'medium',
            label: 'Medium',
            type: 'string'
          },
          {
            value: 'source_medium',
            label: 'Source / Medium',
            type: 'string'
          },
          {
            value: 'web_property_id',
            label: 'Web Property Id',
            type: 'string'
          },
          {
            value: 'account_id',
            label: 'Account Id',
            type: 'string'
          },
          {
            value: 'profile_name',
            label: 'profile name',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-dk-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'denmark'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-se-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'sweden'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-no-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'norway'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-fi-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'finland'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'hostname',
                  value: '.de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: '-de-'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'germany'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    gads: [
      {
        id: 'gadsCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Google Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'customer__descriptive_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'customer__id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign__name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign__id',
            type: 'string'
          },
          {
            label: 'Campaign status',
            value: 'campaign__status',
            type: 'string'
          },
          {
            label: 'Campaign type',
            value: 'campaign__advertising_channel_type',
            type: 'string'
          },
          {
            label: 'Campaign sub type',
            value: 'campaign__advertising_channel_sub_type',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'display'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'display'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Brand',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'brand'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Shopping',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'shopping'
                }
              ]
            }
          },
          {
            name: 'Paid Search - PMax',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'pmax'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'performance max'
                }
              ]
            }
          },
          {
            name: 'Paid Search - nonBrand',
            rules: {
              '0': [
                {
                  operator: 'not_equals',
                  dimension: 'campaign__name',
                  value: 'everything else'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'gadsMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Google Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'customer__descriptive_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'customer__id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign__name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign__id',
            type: 'string'
          },
          {
            label: 'Campaign status',
            value: 'campaign__status',
            type: 'string'
          },
          {
            label: 'Campaign type',
            value: 'campaign__advertising_channel_type',
            type: 'string'
          },
          {
            label: 'Campaign sub type',
            value: 'campaign__advertising_channel_sub_type',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'global'
                }
              ]
            }
          },
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'customer__descriptive_name',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign__name',
                  value: '-de-'
                }
              ]
            }
          }
        ]
      }
    ],
    bing: [
      {
        id: 'bingCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Microsoft Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'AccountName',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'AccountNumber',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'CampaignName',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'CampaignId',
            type: 'string'
          },
          {
            label: 'Campaign type',
            value: 'CampaignType',
            type: 'string'
          },
          {
            label: 'Campaign ad distribution',
            value: 'AdDistribution',
            type: 'string'
          },
          {
            label: 'Network',
            value: 'Network',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'display'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'display'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Brand',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'brand'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Shopping',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'shopping'
                }
              ]
            }
          },
          {
            name: 'Paid Search - nonBrand',
            rules: {
              '0': [
                {
                  operator: 'not_equals',
                  dimension: 'CampaignName',
                  value: 'everything else'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'bingMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Microsoft Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'AccountName',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'AccountNumber',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'CampaignName',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'CampaignId',
            type: 'string'
          },
          {
            label: 'Campaign type',
            value: 'CampaignType',
            type: 'string'
          },
          {
            label: 'Campaign ad distribution',
            value: 'AdDistribution',
            type: 'string'
          },
          {
            label: 'Network',
            value: 'Network',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'AccountName',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'CampaignName',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    facebook: [
      {
        id: 'facebookCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Meta Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign_id',
            type: 'string'
          },
          {
            label: 'Campaign buying type',
            value: 'buying_type',
            type: 'string'
          },
          {
            label: 'Campaign objective',
            value: 'objective',
            type: 'string'
          },
          {
            label: 'Ad set name',
            value: 'adset_name',
            type: 'string'
          },
          {
            label: 'Ad set ID',
            value: 'adset_id',
            type: 'string'
          },
          {
            label: 'Ad name',
            value: 'ad_name',
            type: 'string'
          },
          {
            label: 'Ad ID',
            value: 'ad_id',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Paid Social - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Paid Social - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'prospecting'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'facebookMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Meta Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign_id',
            type: 'string'
          },
          {
            label: 'Campaign objective',
            value: 'objective',
            type: 'string'
          },
          {
            label: 'Ad set name',
            value: 'adset_name',
            type: 'string'
          },
          {
            label: 'Ad set ID',
            value: 'adset_id',
            type: 'string'
          },
          {
            label: 'Ad name',
            value: 'ad_name',
            type: 'string'
          },
          {
            label: 'Ad ID',
            value: 'ad_id',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    dv360: [
      {
        id: 'dv360CustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in DV360.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Partner name',
            value: 'filter_partner_name',
            type: 'string'
          },
          {
            label: 'Partner ID',
            value: 'filter_partner',
            type: 'string'
          },
          {
            label: 'Advertiser name',
            value: 'filter_advertiser_name',
            type: 'string'
          },
          {
            label: 'Advertiser ID',
            value: 'filter_advertiser',
            type: 'string'
          },
          {
            value: 'filter_media_plan_name',
            label: 'Campaign Name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'filter_media_plan',
            type: 'string'
          },
          {
            label: 'Insertion order name',
            value: 'filter_insertion_order_name',
            type: 'string'
          },
          {
            label: 'Insertion order ID',
            value: 'filter_insertion_order',
            type: 'string'
          },
          {
            label: 'Line item name',
            value: 'filter_line_item_name',
            type: 'string'
          },
          {
            label: 'Line item ID',
            value: 'filter_line_item',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'prospecting'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'dv360MarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in DV360.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Partner name',
            value: 'filter_partner_name',
            type: 'string'
          },
          {
            label: 'Partner ID',
            value: 'filter_partner',
            type: 'string'
          },
          {
            label: 'Advertiser name',
            value: 'filter_advertiser_name',
            type: 'string'
          },
          {
            label: 'Advertiser ID',
            value: 'filter_advertiser',
            type: 'string'
          },
          {
            value: 'filter_media_plan_name',
            label: 'Campaign Name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'filter_media_plan',
            type: 'string'
          },
          {
            label: 'Insertion order name',
            value: 'filter_insertion_order_name',
            type: 'string'
          },
          {
            label: 'Insertion order ID',
            value: 'filter_insertion_order',
            type: 'string'
          },
          {
            label: 'Line item name',
            value: 'filter_line_item_name',
            type: 'string'
          },
          {
            label: 'Line item ID',
            value: 'filter_line_item',
            type: 'string'
          },
          {
            label: 'Creative type',
            value: 'filter_creative_type',
            type: 'string'
          },
          {
            label: 'Position in content',
            value: 'filter_position_in_content',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'filter_advertiser_name',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'filter_insertion_order_name',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    snapchat: [
      {
        id: 'snapchatCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Snapchat.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'id',
            type: 'string'
          },
          {
            label: 'Campaign Objective',
            value: 'objective',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'prospecting'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'snapchatMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Snapchat.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'id',
            type: 'string'
          },
          {
            label: 'Campaign Objective',
            value: 'objective',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'name',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    tiktok: [
      {
        id: 'tiktokCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in TikTok.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'advertiser_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign_id',
            type: 'string'
          },
          {
            label: 'Campaign Objective',
            value: 'objective_type',
            type: 'string'
          },
          {
            label: 'Campaign Dedicate Type',
            value: 'campaign_dedicate_type',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'prospecting'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'tiktokMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in TikTok.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'advertiser_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign_id',
            type: 'string'
          },
          {
            label: 'Campaign Objective',
            value: 'objective_type',
            type: 'string'
          },
          {
            label: 'Campaign Dedicate Type',
            value: 'campaign_dedicate_type',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'advertiser_name',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    criteo: [
      {
        id: 'criteoCustomChannelGrouping',
        type: 'case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Criteo.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'Advertiser',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'AdvertiserId',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'Campaign',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'CampaignId',
            type: 'string'
          },
          {
            label: 'Objective',
            value: 'Objective',
            type: 'string'
          },
          {
            label: 'Media type',
            value: 'MediaType',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'matches_regex',
                  dimension: 'Campaign',
                  value: '.*'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'criteoMarketChannelGrouping',
        type: 'case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Criteo.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'Advertiser',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'AdvertiserId',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'Campaign',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'CampaignId',
            type: 'string'
          },
          {
            label: 'Objective',
            value: 'Objective',
            type: 'string'
          },
          {
            label: 'Media type',
            value: 'MediaType',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'Advertiser',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'Advertiser',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'Campaign',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'Campaign',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'Advertiser',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'Advertiser',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'Campaign',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'Campaign',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'Advertiser',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'Advertiser',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'Campaign',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'Campaign',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'Advertiser',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'Advertiser',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'Campaign',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'Campaign',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'Advertiser',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'Advertiser',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'Campaign',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'Campaign',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'Advertiser',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'Campaign',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    amazon: [
      {
        id: 'amazonCustomChannelGrouping',
        type: 'case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Amazon.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign_id',
            type: 'string'
          },
          {
            label: 'Campaign type',
            value: 'campaign_type',
            type: 'string'
          },
          {
            label: 'Campaign sub type',
            value: 'campaign_sub_type',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Sponsored Products',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'campaign_type',
                  value: 'Products'
                }
              ]
            }
          },
          {
            name: 'Sponsored Brands',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'campaign_type',
                  value: 'Brands'
                }
              ]
            }
          },
          {
            name: 'Sponsored Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'campaign_type',
                  value: 'Display'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Sponsored Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'campaign_type',
                  value: 'Display'
                }
              ],
              '1': [
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'prospecting'
                }
              ]
            }
          },
          {
            name: 'Sponsored Display',
            rules: {
              '0': [
                {
                  operator: 'equals',
                  dimension: 'campaign_type',
                  value: 'Display'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'amazonMarketChannelGrouping',
        type: 'case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Amazon.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign_id',
            type: 'string'
          },
          {
            label: 'Campaign type',
            value: 'campaign_type',
            type: 'string'
          },
          {
            label: 'Campaign sub type',
            value: 'campaign_sub_type',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'dk'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'denmark'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-dk-'
                }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'se'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'sweden'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-se-'
                }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'no'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'norway'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-no-'
                }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'fi'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'finland'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-fi-'
                }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'de'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'germany'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: '-de-'
                }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'account_name',
                  value: 'global'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'global'
                }
              ]
            }
          }
        ]
      }
    ],
    pinterest: [
      {
        id: 'pinterestCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in Pinterest.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'ad_account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign_id',
            type: 'string'
          },
          {
            label: 'Campaign objective',
            value: 'campaign_objective_type',
            type: 'string'
          },
          {
            label: 'Ad group name',
            value: 'ad_group_name',
            type: 'string'
          },
          {
            label: 'Ad group ID',
            value: 'ad_group_id',
            type: 'string'
          },
          {
            label: 'Country',
            value: 'targeting_value',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Display - Remarketing',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'remarketing'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'retargeting'
                }
              ]
            }
          },
          {
            name: 'Display - Prospecting',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'prospecting'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'pinterestMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in Pinterest.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Account ID',
            value: 'ad_account_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign_id',
            type: 'string'
          },
          {
            label: 'Campaign objective',
            value: 'campaign_objective_type',
            type: 'string'
          },
          {
            label: 'Ad group name',
            value: 'ad_group_name',
            type: 'string'
          },
          {
            label: 'Ad group ID',
            value: 'ad_group_id',
            type: 'string'
          },
          {
            label: 'Targeting type',
            value: 'targeting_type',
            type: 'string'
          },
          {
            label: 'Targeting value',
            value: 'targeting_value',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                { operator: 'contains', dimension: 'account_name', value: 'denmark' },
                { operator: 'contains', dimension: 'account_name', value: 'dk' },
                { operator: 'contains', dimension: 'campaign_name', value: 'denmark' },
                { operator: 'contains', dimension: 'campaign_name', value: '-dk-' }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                { operator: 'contains', dimension: 'account_name', value: 'sweden' },
                { operator: 'contains', dimension: 'account_name', value: 'se' },
                { operator: 'contains', dimension: 'campaign_name', value: 'sweden' },
                { operator: 'contains', dimension: 'campaign_name', value: '-se-' }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                { operator: 'contains', dimension: 'account_name', value: 'norway' },
                { operator: 'contains', dimension: 'account_name', value: 'no' },
                { operator: 'contains', dimension: 'campaign_name', value: 'norway' },
                { operator: 'contains', dimension: 'campaign_name', value: '-no-' }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                { operator: 'contains', dimension: 'account_name', value: 'finland' },
                { operator: 'contains', dimension: 'account_name', value: 'fi' },
                { operator: 'contains', dimension: 'campaign_name', value: 'finland' },
                { operator: 'contains', dimension: 'campaign_name', value: '-fi-' }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                { operator: 'contains', dimension: 'account_name', value: 'germany' },
                { operator: 'contains', dimension: 'account_name', value: 'de' },
                { operator: 'contains', dimension: 'campaign_name', value: 'germany' },
                { operator: 'contains', dimension: 'campaign_name', value: '-de-' }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                { operator: 'contains', dimension: 'account_name', value: 'global' },
                { operator: 'contains', dimension: 'campaign_name', value: 'global' }
              ]
            }
          }
        ]
      }
    ],
    chatgpt_ads: [
      {
        id: 'chatgptAdsCustomChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Channel Grouping',
        helperText: 'The Channel Grouping that defines your channels in ChatGPT Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          },
          {
            label: 'Ad group ID',
            value: 'ad_group_id',
            type: 'string'
          },
          {
            label: 'Ad group name',
            value: 'ad_group_name',
            type: 'string'
          },
          {
            label: 'Ad ID',
            value: 'ad_id',
            type: 'string'
          },
          {
            label: 'Ad name',
            value: 'ad_name',
            type: 'string'
          },
          {
            label: 'Ad title',
            value: 'ad_title',
            type: 'string'
          },
          {
            label: 'Ad status',
            value: 'ad_status',
            type: 'string'
          },
          {
            label: 'Ad review status',
            value: 'ad_review_status',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Paid Search - Brand',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'brand'
                }
              ]
            }
          },
          {
            name: 'Paid Search - Generic',
            rules: {
              '0': [
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'generic'
                },
                {
                  operator: 'contains',
                  dimension: 'campaign_name',
                  value: 'nonbrand'
                }
              ]
            }
          }
        ]
      },
      {
        id: 'chatgptAdsMarketChannelGrouping',
        type: 'custom_grouping_or_case_statement',
        label: 'Custom Market Grouping',
        helperText: 'The Market Grouping that defines your markets in ChatGPT Ads.',
        required: false,
        mutable: true,
        dimensions: [
          {
            label: 'Account ID',
            value: 'account_id',
            type: 'string'
          },
          {
            label: 'Account name',
            value: 'account_name',
            type: 'string'
          },
          {
            label: 'Campaign ID',
            value: 'campaign_id',
            type: 'string'
          },
          {
            label: 'Campaign name',
            value: 'campaign_name',
            type: 'string'
          },
          {
            label: 'Ad group ID',
            value: 'ad_group_id',
            type: 'string'
          },
          {
            label: 'Ad group name',
            value: 'ad_group_name',
            type: 'string'
          },
          {
            label: 'Ad ID',
            value: 'ad_id',
            type: 'string'
          },
          {
            label: 'Ad name',
            value: 'ad_name',
            type: 'string'
          },
          {
            label: 'Ad title',
            value: 'ad_title',
            type: 'string'
          },
          {
            label: 'Ad status',
            value: 'ad_status',
            type: 'string'
          },
          {
            label: 'Ad review status',
            value: 'ad_review_status',
            type: 'string'
          }
        ],
        defaultValue: [
          {
            name: 'Denmark',
            rules: {
              '0': [
                { operator: 'contains', dimension: 'account_name', value: 'denmark' },
                { operator: 'contains', dimension: 'account_name', value: 'dk' },
                { operator: 'contains', dimension: 'campaign_name', value: 'denmark' },
                { operator: 'contains', dimension: 'campaign_name', value: '-dk-' }
              ]
            }
          },
          {
            name: 'Sweden',
            rules: {
              '0': [
                { operator: 'contains', dimension: 'account_name', value: 'sweden' },
                { operator: 'contains', dimension: 'account_name', value: 'se' },
                { operator: 'contains', dimension: 'campaign_name', value: 'sweden' },
                { operator: 'contains', dimension: 'campaign_name', value: '-se-' }
              ]
            }
          },
          {
            name: 'Norway',
            rules: {
              '0': [
                { operator: 'contains', dimension: 'account_name', value: 'norway' },
                { operator: 'contains', dimension: 'account_name', value: 'no' },
                { operator: 'contains', dimension: 'campaign_name', value: 'norway' },
                { operator: 'contains', dimension: 'campaign_name', value: '-no-' }
              ]
            }
          },
          {
            name: 'Finland',
            rules: {
              '0': [
                { operator: 'contains', dimension: 'account_name', value: 'finland' },
                { operator: 'contains', dimension: 'account_name', value: 'fi' },
                { operator: 'contains', dimension: 'campaign_name', value: 'finland' },
                { operator: 'contains', dimension: 'campaign_name', value: '-fi-' }
              ]
            }
          },
          {
            name: 'Germany',
            rules: {
              '0': [
                { operator: 'contains', dimension: 'account_name', value: 'germany' },
                { operator: 'contains', dimension: 'account_name', value: 'de' },
                { operator: 'contains', dimension: 'campaign_name', value: 'germany' },
                { operator: 'contains', dimension: 'campaign_name', value: '-de-' }
              ]
            }
          },
          {
            name: 'Global',
            rules: {
              '0': [
                { operator: 'contains', dimension: 'account_name', value: 'global' },
                { operator: 'contains', dimension: 'campaign_name', value: 'global' }
              ]
            }
          }
        ]
      }
    ],
    other: [
      {
        id: 'holidayRegion',
        type: 'dropdown',
        defaultValue: 'EMEA',
        options: [
          {
            value: 'GLOBAL',
            label: 'global'
          },
          {
            value: 'NA',
            label: 'North America'
          },
          {
            value: 'JAPAC',
            label: 'Japan and Asia Pacific'
          },
          {
            value: 'EMEA',
            label: 'Europe, the Middle East and Africa'
          },
          {
            value: 'LAC',
            label: 'Latin America and the Caribbean'
          },
          {
            value: 'AT',
            label: 'Austria'
          },
          {
            value: 'AU',
            label: 'Australia'
          },
          {
            value: 'BE',
            label: 'Belgium'
          },
          {
            value: 'CA',
            label: 'Canada'
          },
          {
            value: 'CH',
            label: 'Switzerland'
          },
          {
            value: 'DE',
            label: 'Germany'
          },
          {
            value: 'DK',
            label: 'Denmark'
          },
          {
            value: 'ES',
            label: 'Spain'
          },
          {
            value: 'FI',
            label: 'Finland'
          },
          {
            value: 'FR',
            label: 'France'
          },
          {
            value: 'GB',
            label: 'United Kingdom'
          },
          {
            value: 'IT',
            label: 'Italy'
          },
          {
            value: 'NL',
            label: 'Netherlands'
          },
          {
            value: 'NO',
            label: 'Norway'
          },
          {
            value: 'PL',
            label: 'Poland'
          },
          {
            value: 'PT',
            label: 'Portugal'
          },
          {
            value: 'SE',
            label: 'Sweden'
          },
          {
            value: 'TR',
            label: 'Turkey'
          },
          {
            value: 'US',
            label: 'United States'
          }
        ],
        label: 'Holiday Region',
        tooltip:
          'The Holiday region that matches you area. See https://cloud.google.com/bigquery/docs/reference/standard-sql/bigqueryml-syntax-create-time-series for reference.',
        helperText:
          'The Holiday region is used to determine which holidays that are relevant for your area. This is used to adjust the forecast for holidays. See https://cloud.google.com/bigquery/docs/reference/standard-sql/bigqueryml-syntax-create-time-series for reference.',
        required: true,
        mutable: true
      },
      {
        id: 'ga4MigrationDate',
        type: 'date',
        label: 'GA4 Migration Date',
        helperText:
          'The date where you switched to GA4. Used to determine when to change reporting numbers from UA to GA4. If you did not select any UA data, just leave the field unchanged.',
        required: false,
        defaultValue: '2023-06-30',
        mutable: true
      },
      {
        id: 'targetCurrency',
        type: 'dropdown',
        label: 'Target Currency',
        defaultValue: 'EUR',
        tooltip: 'The currency to transform all currency-based columns (cost, revenue, etc) to.',
        required: true,
        mutable: true
      },
      {
        id: 'ga4Events',
        type: 'string',
        label: 'GA4 Event name',
        helperText: 'Write the name of the GA4 event you want to forecast. For example: "add_to_cart" or "purchase".',
        required: false,
        defaultValue: 'purchase',
        mutable: true
      },
      {
        id: 'gadsConvActions',
        type: 'string',
        label: 'Google ads conversion action name',
        helperText: 'Write the Google ads Conversion action name you want to forecast (case-insensitive)',
        required: false,
        defaultValue: 'purchase',
        mutable: true
      },
      {
        id: 'bingConvActions',
        type: 'string',
        label: 'Bing Goal Name',
        helperText: 'Write the Bing Goal name (conversion name) you want to forecast (case-insensitive)',
        required: false,
        defaultValue: 'purchase',
        mutable: true
      },
      {
        id: 'facebookConvActions',
        type: 'string',
        label: 'Facebook Conversion Name',
        helperText: 'Write the Facebook conversion name you want to forecast (case-insensitive.)',
        required: false,
        defaultValue: 'purchase',
        mutable: true
      },
      {
        id: 'dv360ConvActions',
        type: 'string',
        label: 'DV360 Floodlight Name',
        helperText: 'Write the DV360 floodlight name you want to forecast (case-insensitive.)',
        required: false,
        defaultValue: 'purchase',
        mutable: true
      },
      {
        id: 'snapchatConvActions',
        type: 'string',
        label: 'Snapchat Conversion Name',
        helperText: 'Write the Snapchat conversion name you want to forecast (case-insensitive.)',
        required: false,
        defaultValue: 'conversion_purchases',
        mutable: true
      },
      {
        id: 'tiktokConvActions',
        type: 'string',
        label: 'TikTok Conversion Name',
        helperText: 'Write the TikTok conversion name you want to forecast (case-insensitive)',
        required: false,
        defaultValue: 'complete_payment',
        mutable: true
      },
      {
        id: 'criteoConvActions',
        type: 'string',
        label: 'Criteo Conversion Name',
        helperText: 'Write the Criteo conversion name you want to forecast (case-insensitive)',
        required: false,
        defaultValue: 'SalesClientAttribution',
        mutable: true
      },
      {
        id: 'pinterestConvActions',
        type: 'string',
        label: 'Pinterest Conversion Name',
        helperText: 'Write the Pinterest conversion name you want to forecast (case-insensitive)',
        required: false,
        defaultValue: 'checkout',
        mutable: true
      },
      {
        id: 'chatgptAdsConvActions',
        type: 'dropdown',
        label: 'ChatGPT Ads Conversion Name',
        helperText: 'Select the ChatGPT Ads conversion name you want to forecast.',
        required: false,
        defaultValue: 'conversions',
        mutable: true,
        internalOnly: true,
        options: [
          { value: 'conversions', label: 'Conversions' },
          { value: 'click_through_conversions', label: 'Click-through conversions' },
          { value: 'view_through_conversions', label: 'View-through conversions' }
        ]
      }
    ]
  }
}

export default config
